import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import {
  ApiErrorClass,
  Exercise,
  Plan,
  WorkoutCategories,
} from "@readyfit-common/models";
import {
  parseFitnessWorkout,
  parseRunWorkout,
  parseSwimWorkout,
} from "@readyfit-common/utils";
import { LoadingButton } from "src/grading/components";
import RFModal from "src/grading/components/RFModal/RFModal";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import {
  selectSelectedDate,
  selectSelectedWeek,
  selectSelectedWorkout,
  selectWorkoutModalOpen,
  setWorkoutModalOpen,
  useAddAthleteWorkoutMutation,
  useCopyFromWorkoutLibraryMutation,
  useSaveToWorkoutLibraryMutation,
  useUpdateAthleteWorkoutMutation,
} from "src/store/reducers/training/trainingApiSlice";
import theme from "src/theme";
import AddFromLibraryForm from "../AddFromLibraryForm/AddFromLibraryForm";
import EditWorkoutForm from "../WorkoutForm/WorkoutForm";
import useStyles from "./EditWorkoutModal.styles";

interface EditWorkoutModalProps {
  trainingPlan: Plan;
}

export interface WorkoutFormValues {
  name: string;
  workoutCategoryId: number;
  workoutTypeId: number;
  distance?: number | null;
  estimatedTime?: string | null;
  description?: string;
  exercises?: Exercise[];
  date?: string;
  saveToWorkoutLibrary?: boolean;
  workoutLibraryId?: string;
  tabValue?: number;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().when("tabValue", ([tabValue], schema) => {
    return tabValue === 0
      ? schema.required("Name is required")
      : schema.notRequired();
  }),
  workoutCategoryId: Yup.number().when("tabValue", ([tabValue], schema) => {
    return tabValue === 0
      ? schema.required("Workout category is required")
      : schema.notRequired();
  }),
  workoutTypeId: Yup.number().when("tabValue", ([tabValue], schema) => {
    return tabValue === 0
      ? schema.required("Workout type is required")
      : schema.notRequired();
  }),
  distance: Yup.number().nullable(),
  estimatedTime: Yup.string().nullable(),
  description: Yup.string().required(),
  saveToWorkoutLibrary: Yup.boolean().required(),
});

const EditWorkoutModal = ({ trainingPlan }: EditWorkoutModalProps) => {
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(0);
  const dispatch = useAppDispatch();
  const open = useAppSelector(selectWorkoutModalOpen);
  const selectedWorkout = useAppSelector(selectSelectedWorkout);
  const selectedDate = useAppSelector(selectSelectedDate);
  const selectedWeek = useAppSelector(selectSelectedWeek);
  const dayOfTheWeek = moment(selectedDate).format("dddd");
  const [updateAthleteWorkout, { isLoading: updateAthleteWorkoutLoading }] =
    useUpdateAthleteWorkoutMutation();
  const [addAthleteWorkout, { isLoading: addAthleteWorkoutLoading }] =
    useAddAthleteWorkoutMutation();
  const [saveToWorkoutLibrary, { isLoading: saveToWorkoutLibraryLoading }] =
    useSaveToWorkoutLibraryMutation();
  const [copyFromWorkoutLibrary, { isLoading: copyFromWorkoutLibraryLoading }] =
    useCopyFromWorkoutLibraryMutation();

  const isEdit = useMemo(() => !!selectedWorkout?.id, [selectedWorkout]);

  const handleClose = () => {
    formik.resetForm();
    dispatch(setWorkoutModalOpen(false));
  };

  const initialValues: WorkoutFormValues = {
    name: selectedWorkout?.name || "",
    workoutCategoryId: selectedWorkout?.workoutCategoryId as number,
    workoutTypeId: selectedWorkout?.workoutTypeId as number,
    distance: selectedWorkout?.distance || null,
    estimatedTime: selectedWorkout?.estimatedTime || null,
    description: selectedWorkout?.description || "",
    exercises: selectedWorkout?.exercises || [],
    saveToWorkoutLibrary: false,
    tabValue: 0,
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values: WorkoutFormValues) => {
      let response;
      try {
        if (tabValue === 1) {
          if (!values.workoutLibraryId) {
            throw new ApiErrorClass({
              message: "No Workout Selected",
            });
          }
          if (!selectedWeek?.id) {
            throw new ApiErrorClass({
              message: "No week id",
            });
          }
          if (!selectedDate) {
            throw new ApiErrorClass({
              message: "No date selected",
            });
          }
          response = await copyFromWorkoutLibrary({
            planId: trainingPlan.id,
            weekId: selectedWeek.id,
            date: selectedDate,
            workoutLibraryId: values.workoutLibraryId,
          }).unwrap();
          if (!response?.success)
            throw new ApiErrorClass({
              message: "Error copying workout",
            });
          dispatch(
            showSnackbar({
              open: true,
              message: "Workout copied successfully",
            }),
          );
          handleClose();
          return;
        }

        const workoutPayload = (
          { isTemplate }: { isTemplate: boolean } = { isTemplate: false },
        ) => {
          if (!selectedDate && !selectedWorkout?.date) {
            throw new ApiErrorClass({
              message: "No date selected",
            });
          }

          const date = selectedDate || selectedWorkout?.date || "";

          switch (values.workoutCategoryId) {
            case WorkoutCategories.RUN:
              return parseRunWorkout(values, date, isTemplate);
            case WorkoutCategories.SWIM:
              return parseSwimWorkout(values, date, isTemplate);
            case WorkoutCategories.FITNESS:
              return parseFitnessWorkout(values, date, isTemplate);
            default:
              return values;
          }
        };
        // EDIT WORKOUT
        if (isEdit) {
          if (!selectedWorkout?.id) {
            throw new ApiErrorClass({
              message: "No workout id",
            });
          }

          response = await updateAthleteWorkout({
            planId: trainingPlan.id,
            weekId: selectedWorkout.weekId,
            workoutId: selectedWorkout.id,
            workout: workoutPayload(),
          }).unwrap();
        } else {
          // ADD WORKOUT
          if (!selectedWeek?.id) {
            throw new ApiErrorClass({
              message: "No week id",
            });
          }
          response = await addAthleteWorkout({
            planId: trainingPlan.id,
            weekId: selectedWeek.id,
            workout: workoutPayload(),
          }).unwrap();
        }

        if (values.saveToWorkoutLibrary) {
          await saveToWorkoutLibrary(
            workoutPayload({ isTemplate: true }),
          ).unwrap();
        }

        if (!response?.success)
          throw new Error(response?.message || "Error saving workout");

        dispatch(
          showSnackbar({
            open: true,
            message: "Workout updated successfully",
          }),
        );

        handleClose();
      } catch (error: unknown) {
        const typedError = error as ApiErrorClass;
        console.log("error", error);
        dispatch(
          showSnackbar({
            open: true,
            message: typedError?.data?.message || "Error updating workout",
            type: "error",
          }),
        );
      }
    },
  });

  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    handleChange,
    resetForm,
  } = formik;

  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return (
          <EditWorkoutForm
            values={values}
            errors={errors}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
          />
        );
      case 1:
        return (
          <AddFromLibraryForm
            values={values}
            errors={errors}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            resetForm={resetForm}
          />
        );
      // return <AddWorkoutForm />;
      default:
        return null;
    }
  };

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={handleClose}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        rowGap={theme.spacing(3)}
      >
        <Typography variant="h5" fontSize={18}>
          {isEdit ? "Edit Workout" : "Add Workout"}
        </Typography>

        {/* TABS */}
        <Grid container justifyContent="center">
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => {
              setTabValue(newValue);
              setFieldValue("tabValue", newValue);
            }}
            className={classes.tabs}
            TabIndicatorProps={{
              style: { display: "none" },
            }}
          >
            <Tab
              className={classes.tab}
              label={isEdit ? "Edit workout" : "Add workout"}
            />
            <Tab className={classes.tab} label="Add from library" />
          </Tabs>
        </Grid>

        {/* DATE */}
        <Grid container>
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              textTransform={"uppercase"}
              fontWeight={"bold"}
            >
              {dayOfTheWeek}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="h5"
              textTransform={"uppercase"}
              fontWeight={"bold"}
              fontSize={24}
            >
              {/* November 16, 2025 */}
              {moment(selectedDate).format("MMMM D, YYYY")}
            </Typography>
          </Grid>
        </Grid>

        {/* TAB CONTENT */}
        {renderTabContent()}

        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          marginTop={theme.spacing(4)}
        >
          {tabValue === 0 && (
            <Grid container>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.saveToWorkoutLibrary}
                    onChange={(e) =>
                      setFieldValue("saveToWorkoutLibrary", e.target.checked)
                    }
                  />
                }
                label="Save to workout library"
              />
            </Grid>
          )}

          <Grid item>
            <Button
              color="secondary"
              className={` ${classes.button} ${classes.backButton}`}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Grid>

          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              className={classes.button}
              label="Save"
              onClick={() => handleSubmit()}
              loading={
                updateAthleteWorkoutLoading ||
                addAthleteWorkoutLoading ||
                saveToWorkoutLibraryLoading ||
                copyFromWorkoutLibraryLoading
              }
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default EditWorkoutModal;
